import { dehydrate, QueryClient, useQueryClient } from '@tanstack/react-query';
import { FormikProps } from 'formik';
import { Button, Modal } from 'litto-lib/components/common';
import {
	accInfoText,
	centsToPrice,
	checkoutDateFormat,
	getParameterByName,
	priceParser
} from 'litto-lib/helpers';
import { queryTypes, useQueryStates } from 'litto-lib/hooks';
import {
	AvailableSvg,
	capitalize,
	getStaticMap,
	isEmpty,
	slugToSvg
} from 'litto-lib/utils';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useRef, useState } from 'react';
import useInView from 'react-cool-inview';
import { BeatLoader } from 'react-spinners';

import useEvent from 'analytics/hooks/useEvent';
import { AccommodationHero, AmenityList } from 'components/accommodation';
import { LocationCard } from 'components/location';
import { CartSummary } from 'components/order';
import { PersonDisplay } from 'components/person';
import {
	Card,
	FeatureItem,
	HeaderLink,
	IReservationFormData,
	LocationSlider,
	ReadMore,
	ReservationForm,
	RowLayout,
	SingleHeroSkeleton,
	TipsSlider
} from 'components/shared';
import { useOrderDispatchContext, useOrderStateContext } from 'context';
import { BasicLayout } from 'Layout';
import { AccommodationJsonLd, getHostname, NextSeo } from 'seo';
import {
	getAccommodation,
	getAccommodationAssociations,
	getAccommodations,
	useGetAccommodationAssociationsQuery,
	useGetAccommodationQuery
} from 'services/shop/accommodation';
import { IAccommodationAssociationRead } from 'services/shop/accommodation/interfaces';
import { getImage } from 'services/shop/helpers';
import {
	HydraCollectionResponse,
	ILocationRead
} from 'services/shop/interfaces';
import { useGetLocationsQuery } from 'services/shop/location';
import {
	createOrder,
	ensureOrderKey,
	useCreateOrderPreviewMutation,
	useUpdateOrderItemMutation
} from 'services/shop/order';
import { getPromoBannerData } from 'services/wordpress/data';
import { IPromoBanner } from 'services/wordpress/data/interface';

export const houseRules = {
	isSelfCheckinPossible: {
		code: 'self_checkin_possible',
		name: 'Self check-in',
		getName: true
	},
	cancellationPolicy: {
		code: 'cancellation_policy',
		name: 'Cancellation policy',
		getName: false
	},
	entireHome: {
		code: 'entire_home',
		name: 'Entire home',
		getName: true
	},
	houseRules: {
		code: 'house_rules',
		name: 'House rules',
		getName: false
	},
	mainTaxon: {
		code: 'property_type',
		name: 'Property type',
		getName: false
	}
};

interface IAccommodationProps {
	accommodationCode: string;
	accommodationAssociationsData: HydraCollectionResponse<IAccommodationAssociationRead>;
	promoBanner: IPromoBanner[];
}

const StickyNav = ({
	calendarRef,
	refsInView,
	amenitiesAnchor,
	guestFormAnchor,
	locationAnchor,
	onReservationSubmit,
	singleHeroInView,
	guestFormInView,
	orderPreview,
	checkin,
	checkout
}: {
	calendarRef: React.MutableRefObject<any>;
	refsInView: any;
	amenitiesAnchor: React.MutableRefObject<any>;
	guestFormAnchor: React.MutableRefObject<any>;
	locationAnchor: React.MutableRefObject<any>;
	onReservationSubmit: () => void;
	singleHeroInView: boolean;
	guestFormInView: boolean;
	orderPreview: any;
	checkin: string | null;
	checkout: string | null;
}) => {
	const items = [
		{
			onClick: () => window.scrollTo(0, 0),
			label: 'Photos'
		},
		{
			onClick: () => scrollToWithOffset(amenitiesAnchor.current),
			label: 'Amenities'
		},
		{
			onClick: () => scrollToWithOffset(locationAnchor.current),
			label: 'Location'
		}
	];

	const lastInView = refsInView[0];

	const openCalendar = () => {
		calendarRef.current.openCalendar();
		scrollToWithOffset(guestFormAnchor.current);
	};

	const scrollToWithOffset = (el: HTMLElement, offset = -140) => {
		window.scroll(
			0,
			el.getBoundingClientRect().top + window.pageYOffset + offset
		);
	};

	return (
		<header
			className={`
				fixed bottom-0 z-[60] h-20 w-full transform bg-white shadow transition-all duration-300 md:top-0
				${
					!amenitiesAnchor.current || singleHeroInView || guestFormInView
						? 'translate-y-full transform opacity-0 md:-translate-y-full'
						: ''
				}`}
		>
			<nav className="container relative flex h-full w-full">
				<ul className="flex h-full flex-1 list-none">
					{items.map(item => {
						return (
							<HeaderLink
								key={item.label}
								indicatorPosition="top"
								onClick={item.onClick}
								className="mr-8 hidden md:flex"
								isActive={item.label == lastInView}
							>
								{item.label}
							</HeaderLink>
						);
					})}
					<div className="flex w-full items-center justify-between text-base md:ml-auto md:w-auto">
						{orderPreview && checkin && checkout && (
							<span className="flex flex-col items-end">
								<span>
									<span className="font-extrabold">{`${centsToPrice(
										orderPreview.averagePricePerDay
									)} €`}</span>
									&nbsp;/ night
								</span>
								<button
									className="underline"
									onClick={() => scrollToWithOffset(guestFormAnchor.current)}
								>
									{priceParser(orderPreview.total)} total
								</button>
							</span>
						)}
						<Button
							onClick={
								orderPreview && checkin && checkout
									? onReservationSubmit
									: openCalendar
							}
							className="my-3 ml-4"
							text={`${
								orderPreview && checkin && checkout
									? 'Book now'
									: 'Select dates'
							}`}
							{...{
								'data-calendar-skip-close':
									orderPreview && checkin && checkout ? null : true
							}}
							withArrow
						/>
					</div>
				</ul>
			</nav>
		</header>
	);
};

const Accommodation: React.FC<IAccommodationProps> = ({
	accommodationCode,
	promoBanner
}) => {
	const { event } = useEvent();
	const router = useRouter();
	const queryClient = useQueryClient();
	const [queries, updateQueries] = useQueryStates(
		{
			adults: queryTypes.integer,
			children: queryTypes.integer,
			infants: queryTypes.integer,
			checkin: queryTypes.string,
			checkout: queryTypes.string
		},
		{ history: 'replace', transitionOptions: { scroll: false } }
	);
	const { checkin, checkout, adults, children, infants } = queries;
	const [clientCheckin, setClientCheckin] = React.useState(checkin);
	const [clientCheckout, setClientCheckout] = React.useState(checkout);

	React.useEffect(() => {
		updateQueries({
			checkin: clientCheckin,
			checkout: clientCheckout
		});
	}, [clientCheckin, clientCheckout]);

	const orderState = useOrderStateContext();
	const orderDispatch = useOrderDispatchContext();

	const [modalAmenityOpen, setModalAmenityOpen] = useState(false);
	const [modalSimilarAcc, setModalSimilarAccOpen] = useState(false);

	const [showPriceDetailsModal, setShowPriceDetailsModal] =
		React.useState<boolean>(false);

	const reservationFormRef: React.Ref<
		FormikProps<IReservationFormData>
	> | null = React.useRef(null);
	const calendarRef = useRef();
	const guestsRef = useRef(null);

	const guestFormAnchor = useRef<HTMLDivElement>(null);
	const singleHeroAnchor = useRef<HTMLDivElement>(null);
	const amenitiesAnchor = useRef<HTMLDivElement>(null);
	const locationAnchor = useRef<HTMLDivElement>(null);

	const { data: accommodation, isLoading: isLoadingAccommodation } =
		useGetAccommodationQuery(
			(router?.query?.code as string) || accommodationCode,
			{
				enabled: !!((router?.query?.code as string) || accommodationCode)
			}
		);

	React.useEffect(() => {
		if (!isLoadingAccommodation && accommodation) {
			event('view_item', {
				currency: 'EUR',
				value: `${
					orderPreview?.averagePricePerDay
						? centsToPrice(orderPreview?.averagePricePerDay)
						: 'Dates unselected'
				}`,
				items: [
					{
						item_name: `${accommodation?.translation?.name}`,
						item_id: `${accommodation?.code}`,
						price: `${
							orderPreview?.averagePricePerDay
								? centsToPrice(orderPreview?.averagePricePerDay)
								: 'Dates unselected'
						}`,
						item_category: `${accommodation?.mainTaxon?.translation?.name}`,
						item_brand: `Accommodation`,
						item_variant: 'Available'
					}
				]
			});
		}
	}, [isLoadingAccommodation, accommodation]);

	const seoTitle = React.useMemo(() => {
		if (!accommodation) return 'LITTO';
		const name = accommodation?.translation?.name;
		const type = accommodation.mainTaxon?.translation.name.slice(0, -1);
		const location = accommodation.location.translation.name;
		const county = accommodation.location.county;

		return `${name} | ${type} in ${location}, ${county}, Croatia - LITTO`;
	}, [accommodation]);

	React.useEffect(() => {
		if (checkin && checkout && accommodation?.code) {
			mutateOrderPreview({
				productCode: accommodation.code,
				dateFrom: checkin
					? moment(checkin, checkoutDateFormat).format('YYYY-MM-DD')
					: null,
				dateTo: checkout
					? moment(checkout, checkoutDateFormat).format('YYYY-MM-DD')
					: null,
				adults: adults || 1,
				children: children || 0,
				infants: infants || 0
			});
		}
	}, [accommodation?.code]);

	const onReservationFormChange = (reservationData: IReservationFormData) => {
		const { dateFrom, dateTo, adults, children, infants } = reservationData;
		const checkin = dateFrom
			? moment(dateFrom).format(checkoutDateFormat)
			: null;
		const checkout = dateTo ? moment(dateTo).format(checkoutDateFormat) : null;

		setClientCheckin(checkin);
		setClientCheckout(checkout);

		updateQueries({
			adults: adults || null,
			children: children || null,
			infants: infants || null
		});
		if (dateFrom && dateTo) {
			mutateOrderPreview(reservationData);
		}
	};

	const openCalendar = () => {
		calendarRef.current && (calendarRef.current as any).openCalendar();
	};

	const closeActiveReservationPoppers = () => {
		(calendarRef.current as any)?.closeCalendar();
		(guestsRef?.current as any)?.closePopper();
	};

	const { data: accommodationAssociations } =
		useGetAccommodationAssociationsQuery(
			accommodationCode,
			{},
			{
				enabled: !!accommodationCode
			}
		);

	const {
		data: orderPreview,
		mutate: mutateOrderPreview,
		isLoading: isLoadingPreview
	} = useCreateOrderPreviewMutation(queryClient);
	const { mutate: mutateUpdateOrderItem } = useUpdateOrderItemMutation();

	const [isSubmittingReservation, setIsSubmittingReservation] =
		React.useState<boolean>(false);
	const onReservationSubmit = async (
		formData: IReservationFormData
		// formikHelpers: FormikHelpers<IReservationFormData>
	) => {
		setIsSubmittingReservation(true);

		const orderKey = await ensureOrderKey(orderState, orderDispatch);
		mutateUpdateOrderItem(
			{ orderKey: orderKey, data: formData },
			{
				onError: async e => {
					if (e?.response?.status === 404) {
						const newOrder = await createOrder(orderDispatch);
						mutateUpdateOrderItem(
							{ orderKey: newOrder.tokenValue, data: formData },
							{
								onSuccess: () => {
									router.push(
										'/checkout/personal-info/[orderKey]',
										`/checkout/personal-info/${newOrder.tokenValue}`
									);
								},
								onSettled: () => {
									setIsSubmittingReservation(false);
								}
							}
						);
					}
					setIsSubmittingReservation(false);
				},
				onSuccess: order => {
					setIsSubmittingReservation(false);
					router.push(
						'/checkout/personal-info/[orderKey]',
						`/checkout/personal-info/${orderKey}`
					);
					if (order) {
						const { product, averagePricePerDay, items, total } = order;
						event('add_to_cart', {
							currency: 'EUR',
							value: `${centsToPrice(total)}`,
							items: [
								{
									item_name: `${product?.translation?.name}`,
									item_id: `${product?.code}`,
									price: `${centsToPrice(averagePricePerDay || 0)}`,
									item_category: `${accommodation?.mainTaxon?.translation?.name}`,
									item_brand: `Accommodation`,
									item_variant: 'Available',
									quantity: `${items.length}`
								}
							]
						});
						event('begin_checkout', {
							currency: 'EUR',
							value: `${centsToPrice(total)}`,
							items: [
								{
									item_name: `${product?.translation?.name}`,
									item_id: `${product?.code}`,
									price: `${centsToPrice(averagePricePerDay || 0)}`,
									item_category: `${accommodation?.mainTaxon?.translation?.name}`,
									item_brand: `Accommodation`,
									item_variant: 'Available',
									quantity: `${items.length}`
								}
							]
						});
					}
				}
			}
		);
	};

	const { data: cities } = useGetLocationsQuery();
	const [otherCities, setOtherCities] = React.useState<ILocationRead[]>([]);

	React.useEffect(() => {
		if (cities && !isEmpty(cities['hydra:member'])) {
			setOtherCities(
				cities['hydra:member'].filter(
					city => city.code !== accommodation?.location?.code
				)
			);
		}
	}, [cities]);

	const { inView: guestFormInView, observe: observeGuestForm } = useInView({
		ref: guestFormAnchor,
		onEnter: () => setRefsInView([...refsInView, 'guestForm']),
		onLeave: () => {
			closeActiveReservationPoppers();
			setRefsInView(refsInView.filter(el => el !== 'guestForm'));
		}
	});

	observeGuestForm(guestFormAnchor.current || undefined);

	const { inView: singleHeroInView, observe: observeSingleHero } = useInView();
	observeSingleHero(singleHeroAnchor.current || undefined);

	const { observe: observeAmenities } = useInView({
		onEnter: () => setRefsInView([...refsInView, 'Amenities']),
		onLeave: () => setRefsInView(refsInView.filter(el => el !== 'Amenities'))
	});

	const { observe: observeLocation } = useInView({
		onEnter: () => setRefsInView([...refsInView, 'Location']),
		onLeave: () => setRefsInView(refsInView.filter(el => el !== 'Location'))
	});

	observeAmenities(amenitiesAnchor.current || undefined);
	observeLocation(locationAnchor.current || undefined);

	const [refsInView, setRefsInView] = useState<string[]>([]);

	if (router.isFallback) {
		return (
			<BasicLayout>
				<div className="mt-6 overflow-x-hidden lg:mt-16">
					<div className="container mb:5 md:mb-10">
						<SingleHeroSkeleton />
					</div>
				</div>
			</BasicLayout>
		);
	}

	if (!accommodation) {
		return (
			<BasicLayout>
				<div className="container mb:5 md:mb-10">
					<SingleHeroSkeleton />
				</div>
			</BasicLayout>
		);
	}

	return (
		<BasicLayout>
			<NextSeo
				title={seoTitle}
				description={`It doesn't get any cheaper than this! Click here to book ${accommodation?.translation?.name} for the best price on the market and complete your holiday in Croatia.`}
				openGraph={{
					type: 'website',
					title: accommodation?.translation?.name,
					description: accommodation?.translation?.shortDescription,
					url: `${getHostname()}/accommodations/${accommodation?.code}`,
					images: [
						{
							url: accommodation?.mainImage?.imageUrl || '',
							width: 800,
							height: 600,
							alt: accommodation?.translation?.name
						}
					]
				}}
			/>
			<AccommodationJsonLd
				name={accommodation?.translation?.name}
				description={`${accommodation?.translation?.shortDescription}`}
				numberOfBedrooms={accommodation?.numberOfBedrooms}
				numberOfBathroomsTotal={accommodation?.numberOfBaths}
				url={`${getHostname()}/accommodations/${accommodation?.code}`}
				image={accommodation?.mainImage?.imageUrl}
				hasMap={getStaticMap(accommodation?.latitude, accommodation?.longitude)}
				latitude={`${accommodation?.latitude}`}
				longitude={`${accommodation?.longitude}`}
				address={{
					addressCountry: accommodation?.location?.country,
					addressLocality: accommodation?.location?.county,
					postalCode: accommodation?.location?.postalCode
				}}
			/>

			<StickyNav
				{...{
					calendarRef,
					refsInView,
					amenitiesAnchor,
					guestFormAnchor,
					locationAnchor,
					singleHeroInView,
					guestFormInView,
					onReservationSubmit: () => reservationFormRef?.current?.submitForm(),
					orderPreview,
					checkin,
					checkout
				}}
			/>
			<div className="mt-6 overflow-x-hidden lg:mt-16">
				<div ref={singleHeroAnchor} className="container mb:5 md:mb-10">
					<AccommodationHero accommodation={accommodation} />
					<hr className="my-5 md:hidden" />
				</div>

				<div className="container">
					<div ref={guestFormAnchor} />

					<RowLayout
						breakOn="md"
						subtitle="Average nightly rate is rounded"
						title={
							(checkin &&
								checkout &&
								(orderPreview?.averagePricePerDay ? (
									`${centsToPrice(orderPreview?.averagePricePerDay)} € / night`
								) : (
									<BeatLoader
										loading={true}
										size={10}
										css={`
											text-primary-500
										`}
									/>
								))) ||
							'Add dates for prices'
						}
						// subtitle={`Minimum stay is ${accommodation.minStay} ${//ovdje ide variant -> minStay
						// 	accommodation.minStay == 1 ? 'day' : 'days'
						// }.`}
					>
						<div className="items-start md:flex">
							<div className="w-full md:mr-2.5 md:w-3/8">
								<ReservationForm
									formRef={reservationFormRef}
									calendarRef={calendarRef}
									guestsRef={guestsRef}
									accommodationCode={accommodation.code}
									initialValues={{
										productCode: accommodation.code,
										dateFrom:
											(checkin &&
												moment(clientCheckin, checkoutDateFormat).format(
													'YYYY-MM-DD'
												)) ||
											'',
										dateTo:
											(checkout &&
												moment(clientCheckout, checkoutDateFormat).format(
													'YYYY-MM-DD'
												)) ||
											'',
										adults: adults || 1,
										children: children || 0,
										infants: infants || 0
									}}
									checkInDate={
										clientCheckin
											? moment(clientCheckin, checkoutDateFormat)
											: null
									}
									checkOutDate={
										clientCheckout
											? moment(clientCheckout, checkoutDateFormat)
											: null
									}
									// minimumNights={accommodation.minStay || 1}//ovdje ide variant -> minStay
									maxGuests={accommodation.numberOfGuests}
									maxInfants={accommodation.numberOfInfants}
									onChange={onReservationFormChange}
									onSubmit={values => {
										orderPreview && checkin && checkout
											? onReservationSubmit(values)
											: openCalendar();
									}}
									isLoading={isSubmittingReservation}
								/>
							</div>

							<CartSummary
								boxed
								priceDetailsModalState={[
									showPriceDetailsModal,
									setShowPriceDetailsModal
								]}
								className="mt-6 md:mt-0 md:w-5/8"
								skeleton={!orderPreview && isLoadingPreview}
								{...(orderPreview && checkin && checkout
									? { order: orderPreview }
									: {})}
							/>
						</div>
					</RowLayout>
					{!isEmpty(Object.keys(accommodation?.goodToKnow || {})) && (
						<RowLayout title="Good to know">
							{/* @todo: this should all be refactored */}
							{(
								Object.keys(
									accommodation.goodToKnow
								) as unknown as (keyof typeof houseRules)[]
							).map(key => {
								let ruleText;
								const { code, name, getName } = houseRules[key];
								const Svg = slugToSvg(code as AvailableSvg);

								if (getName) {
									ruleText =
										accInfoText[code as keyof typeof accInfoText][
											accommodation.goodToKnow[key] ? 0 : 1
										];
									// ruleText = (accInfoText[((accommodation.goodToKnow as any)[key as] as any)] as any)[code];
								} else {
									ruleText = capitalize((accommodation.goodToKnow as any)[key]);
								}

								return (
									<FeatureItem
										icon={<Svg />}
										key={code}
										title={name}
										text={ruleText}
										withBg
										primary
										className="mb-5 last:mb-0"
									/>
								);
							})}
						</RowLayout>
					)}

					<div ref={amenitiesAnchor} />
					{!isEmpty(accommodation?.highlightedAmenities || []) && (
						<RowLayout title="Amenities">
							<div className="md:columns-2">
								{accommodation.highlightedAmenities.map(highlightedAmenity => {
									const { code, name } = highlightedAmenity;
									const Svg = slugToSvg(code as AvailableSvg);
									return (
										<FeatureItem
											withBg
											icon={<Svg />}
											key={code}
											text={name}
											className="mb-5 last:mb-0"
										/>
									);
								})}
							</div>
							<Button
								className="mt-5"
								onClick={() => {
									setModalAmenityOpen(true);
								}}
								size="sm"
								text="Show all amenities"
								color="primary"
							/>
						</RowLayout>
					)}

					{!!accommodation?.translation?.description?.length && (
						<RowLayout title="Detailed description">
							<div className="prose-lg prose">
								<ReadMore
									limit={500}
									htmlContent={accommodation.translation.description}
								/>
							</div>
						</RowLayout>
					)}
					<div ref={locationAnchor} />
					<RowLayout
						title="Location"
						subtitle={accommodation?.location?.translation?.title}
					>
						<LocationCard
							lat={accommodation?.latitude}
							lng={accommodation?.longitude}
							neighborhood={accommodation?.translation.neighborhood}
							zoom={16}
						/>
					</RowLayout>

					<RowLayout title="Your host">
						<PersonDisplay
							languages={[
								...(accommodation?.primaryLanguage
									? [accommodation?.primaryLanguage]
									: []),
								...accommodation?.secondaryLanguages
							]}
							imgSrc={accommodation?.owner?.avatar?.avatarUrl}
							size="lg"
							name={`${accommodation?.owner.firstName} ${accommodation?.owner?.lastName}`}
							info={accommodation?.owner?.bio}
						/>
					</RowLayout>
				</div>
				<div className="mb-20">
					<div className="container">
						<hr className="mb-10" />
						<div className="flex flex-col items-start md:-mx-2.5 md:flex-row">
							{!isEmpty(accommodation?.accommodationRules || []) && (
								<div className="mb-12 md:mb-0 md:w-1/3 md:px-2.5">
									<h3 className="mb-4 text-lg text-gray-500">House rules</h3>
									{accommodation.accommodationRules.map(rule => {
										const { code, name, value } = rule;
										let valueText;
										if (typeof value === 'boolean') {
											valueText = `${
												accInfoText[code as keyof typeof accInfoText][
													value === true ? 0 : 1
												]
											}`;
										} else {
											valueText = `${name} ${value}`;
										}
										const Svg = slugToSvg(code as AvailableSvg);
										return (
											<FeatureItem
												key={name}
												className="mb-2.5 last:mb-0"
												icon={<Svg />}
												text={valueText}
												size="sm"
											/>
										);
									})}
								</div>
							)}

							{!isEmpty(accommodation?.accommodationHealthAndSafety || []) && (
								<div className="md:w-1/3 md:px-2.5">
									<h3 className="mb-4 text-lg text-gray-500">
										Health and safety
									</h3>
									{accommodation.accommodationHealthAndSafety.map(rule => {
										const { code, name } = rule;
										const Svg = slugToSvg(code as AvailableSvg);
										return (
											<FeatureItem
												key={code}
												size="sm"
												className="mb-2.5 last:mb-0"
												icon={<Svg />}
												text={name}
											/>
										);
									})}
								</div>
							)}
							{!!accommodation?.cancellationPolicyWithDescription
								?.description && (
								<div className="md:w-1/3 md:px-2.5">
									<h3 className="mb-4 text-lg text-gray-500">
										Cancellation policy
									</h3>
									<p
										className="mb-4"
										dangerouslySetInnerHTML={{
											__html:
												accommodation.cancellationPolicyWithDescription
													.description
										}}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
				{accommodationAssociations &&
					!isEmpty(accommodationAssociations['hydra:member']) && (
						<React.Fragment>
							<div className="bg-gray-50 pt-16 pb-20">
								<div className="container">
									<h2 className="mb-8 text-xl font-bold">
										Similar accommodations
									</h2>
									<div className="-mx-2.5 flex flex-row flex-wrap">
										{accommodationAssociations[
											'hydra:member'
										][0].associatedProducts
											.slice(0, 4)
											.map(association => {
												const {
													'@id': id,
													basePrice,
													numberOfGuests,
													numberOfBedrooms,
													mainImage,
													translation
												} = association;
												const { name, slug } = translation;
												return (
													<Card
														className="mb-5 w-1/2 px-2.5 pb-5 md:mb-0 md:w-1/4 md:pb-0"
														key={id}
														href={`/accommodations/${slug}`}
														meta={`${numberOfGuests} guests - ${numberOfBedrooms} bedrooms`}
														title={name}
														imgSrc={getImage(mainImage.image_urls, 'LG')}
														info={`${priceParser(basePrice)}/person`}
													/>
												);
											})}
									</div>
									<Button
										size="sm"
										color="primary"
										className="mt-6"
										text="Show all stays"
										onClick={() => {
											setModalSimilarAccOpen(true);
										}}
									/>
								</div>
							</div>

							<Modal
								hideFooter
								isOpen={modalSimilarAcc}
								headerText="Similar accommodations"
								onRequestClose={() => {
									setModalSimilarAccOpen(false);
								}}
							>
								<div className="-mx-2.5 flex flex-wrap">
									{accommodationAssociations[
										'hydra:member'
									][0].associatedProducts.map(association => {
										const {
											'@id': id,
											basePrice,
											numberOfGuests,
											numberOfBedrooms,
											mainImage,
											translation
										} = association;
										const { name, slug } = translation;
										return (
											<Card
												className="mb-8 w-1/2 px-2.5 last:mb-0 sm:mb-9.5"
												key={id}
												href={`/accommodations/${slug}`}
												meta={`${numberOfGuests} guests - ${numberOfBedrooms} bedrooms`}
												title={name}
												imgSrc={getImage(mainImage.image_urls, 'LG')}
												info={`${priceParser(basePrice)}/person`}
											/>
										);
									})}
								</div>
							</Modal>
						</React.Fragment>
					)}

				{promoBanner && <TipsSlider tipsArray={promoBanner} />}

				<h2 className="container mt-20 mb-10 text-2xl">
					Didn’t find anything breathtaking? Explore similar cities!
				</h2>

				<LocationSlider className="mb-20" cities={otherCities.slice(0, 4)} />

				<Modal
					hideFooter
					isOpen={modalAmenityOpen}
					headerText="Amenities"
					onRequestClose={() => {
						setModalAmenityOpen(false);
					}}
				>
					<AmenityList
						numOfColumns={2}
						amenities={accommodation?.amenities || {}}
					/>
				</Modal>
			</div>
		</BasicLayout>
	);
};

export default Accommodation;

export async function getStaticPaths() {
	if (process.env.ENV === 'staging' || process.env.NODE_ENV === 'development') {
		return { paths: [], fallback: true };
	}

	let paths: { params: { code: string } }[] = [];
	let accommodations = await getAccommodations({
		params: { itemsPerPage: 25 }
	});
	const lastPage = Number(
		getParameterByName('page', accommodations['hydra:view']['hydra:last']) || 1
	);
	let currentPage = 1;
	while (currentPage <= lastPage) {
		const accommodationSlugs = accommodations['hydra:member'].map(
			accommodation => {
				return {
					params: { code: accommodation?.code }
				};
			}
		);
		const page =
			getParameterByName('page', accommodations['hydra:view']['hydra:next']) ||
			1;
		currentPage++;

		paths = paths.concat(accommodationSlugs);
		accommodations = await getAccommodations({
			params: { page: Number(page), itemsPerPage: 25 }
		});
	}

	return {
		paths,
		fallback: true
	};
}

export async function getStaticProps({ params }: { params: { code: string } }) {
	const { code } = params;
	try {
		console.time(`: /accommodations/${code}`);
		const queryClient = new QueryClient();

		const accommodation = await getAccommodation(code);
		if (!accommodation) {
			throw new Error(`Accommodation with code '${code}' not found`);
		}
		const accommodationAssociations = await getAccommodationAssociations(code);

		const dataPromoBanner = await getPromoBannerData();

		await Promise.all([
			queryClient.prefetchQuery(['accommodation', code], () => {
				return accommodation;
			}),
			queryClient.prefetchQuery(['accommodation_associations', code], () => {
				return accommodationAssociations;
			})
		]);
		console.timeEnd(`: /accommodations/${code}`);

		return {
			props: {
				accommodationCode: code,
				accommodation,
				promoBanner: dataPromoBanner.acf.promo_banner,
				dehydratedState: dehydrate(queryClient)
			},
			revalidate: 60
		};
	} catch (e) {
		return {
			props: {
				accommodationCode: code
			},
			notFound: true
		};
	}
}
